import type { ProductRow, FinishingRow, MoneyPair } from '@/pages/quotation-design/types';
import DualCurrencyMoney from '@/components/base/DualCurrencyMoney';
import SectionTitle from './SectionTitle';

function MoneyValue({
  value,
  primaryClassName,
  secondaryClassName,
}: {
  value: MoneyPair;
  primaryClassName?: string;
  secondaryClassName?: string;
}) {
  return (
    <DualCurrencyMoney
      usdAmount={value.usd}
      selectedAmount={value.selected}
      selectedCurrency={value.currency}
      preserveUsdPrecision={value.unitPrice}
      secondaryPosition="inline"
      primaryClassName={primaryClassName}
      secondaryClassName={secondaryClassName}
    />
  );
}

function FinishingBlock({
  finishing,
  index,
}: {
  finishing: FinishingRow;
  index: number;
}) {
  return (
    <div className="mt-3 pl-4 md:pl-8 border-l-2 border-ink-100">
      <div className="grid grid-cols-12 gap-x-4 items-start">
        <div className="col-span-12 md:col-span-5">
          <span className="text-[10px] font-bold text-ink-400 uppercase tracking-wider">
            Decoration 0{index}
          </span>
          <p className="text-sm font-semibold text-ink-700 mt-0.5">{finishing.method}</p>
          <p className="text-xs text-ink-500 mt-0.5">
            <span className="text-ink-400">{finishing.colorLabel}:</span>{' '}
            {finishing.colorValue}
          </p>
        </div>
        <div className="col-span-4 md:col-span-2 md:text-right text-xs text-ink-600 mt-1 md:mt-0">
          <span className="md:hidden text-[10px] text-ink-400 uppercase block">수량</span>
          {finishing.quantity.toLocaleString('en-US')} pcs
        </div>
        <div className="col-span-4 md:col-span-2 md:text-right mt-1 md:mt-0">
          <span className="md:hidden text-[10px] text-ink-400 uppercase block">단가</span>
          <MoneyValue
            value={finishing.unitPrice}
            primaryClassName="text-xs text-ink-600 font-mono"
            secondaryClassName="text-[10px] text-ink-400 font-normal font-sans"
          />
        </div>
        <div className="col-span-4 md:col-span-3 md:text-right mt-1 md:mt-0 tabular-nums">
          <span className="md:hidden text-[10px] text-ink-400 uppercase block font-sans">금액</span>
          <MoneyValue
            value={finishing.amount}
            primaryClassName="text-sm font-semibold text-ink-700"
            secondaryClassName="text-[11px] text-ink-400 font-normal"
          />
        </div>
      </div>
    </div>
  );
}

function ProductBlock({ product, index }: { product: ProductRow; index: number }) {
  const specs = [
    product.material && `Material: ${product.material}`,
    product.capacity && `Capacity: ${product.capacity}`,
    product.size && `Size: ${product.size}`,
  ].filter(Boolean) as string[];

  return (
    <div className="px-6 md:px-9 py-5 border-t border-ink-100">
      <div className="grid grid-cols-12 gap-x-4 items-start">
        <div className="col-span-12 md:col-span-5 flex items-start gap-3">
          {product.imageUrl && (
            <div className="w-12 h-12 md:w-14 md:h-14 shrink-0 bg-cream-50 border border-ink-100 rounded-md overflow-hidden flex items-center justify-center">
              <img
                src={product.imageUrl}
                alt={product.name}
                className="w-full h-full object-contain object-top"
              />
            </div>
          )}
          <div className="min-w-0">
            <span className="text-[10px] font-bold text-warm-600 uppercase tracking-wider">
              Product 0{index}
            </span>
            <p className="text-sm md:text-[15px] font-bold text-ink-700 leading-snug">
              {product.name}
            </p>
            <p className="text-xs text-ink-400 font-mono mt-0.5">SKU: {product.sku}</p>
            {specs.length > 0 && (
              <p className="text-xs text-ink-500 mt-1">{specs.join(' · ')}</p>
            )}
          </div>
        </div>
        <div className="col-span-4 md:col-span-2 md:text-right text-xs text-ink-600 mt-2 md:mt-0">
          <span className="md:hidden text-[10px] text-ink-400 uppercase block">수량</span>
          {product.quantity.toLocaleString('en-US')} pcs
        </div>
        <div className="col-span-4 md:col-span-2 md:text-right mt-2 md:mt-0">
          <span className="md:hidden text-[10px] text-ink-400 uppercase block">단가</span>
          <MoneyValue
            value={product.unitPrice}
            primaryClassName="text-xs text-ink-600 font-mono"
            secondaryClassName="text-[10px] text-ink-400 font-normal font-sans"
          />
        </div>
        <div className="col-span-4 md:col-span-3 md:text-right mt-2 md:mt-0 tabular-nums">
          <span className="md:hidden text-[10px] text-ink-400 uppercase block font-sans">금액</span>
          <MoneyValue
            value={product.amount}
            primaryClassName="text-sm font-bold text-ink-700"
            secondaryClassName="text-[11px] text-ink-400 font-normal"
          />
        </div>
      </div>

      {product.finishings.map((f, i) => (
        <FinishingBlock key={`${f.method}-${f.colorValue}`} finishing={f} index={i + 1} />
      ))}
    </div>
  );
}

export default function ProductQuoteSection({ products }: { products: ProductRow[] }) {
  return (
    <div className="mt-8">
      <div className="px-6 md:px-9">
        <SectionTitle>Product &amp; Decoration / Finishing</SectionTitle>
      </div>

      {/* Column headers (desktop) */}
      <div className="hidden md:grid grid-cols-12 gap-x-4 px-6 md:px-9 mt-4 text-[10px] font-semibold uppercase tracking-wider text-ink-400">
        <span className="col-span-5">Item</span>
        <span className="col-span-2 text-right">Quantity</span>
        <span className="col-span-2 text-right">Unit Price</span>
        <span className="col-span-3 text-right">Amount</span>
      </div>

      <div className="mt-2">
        {products.map((p, i) => (
          <ProductBlock key={p.sku} product={p} index={i + 1} />
        ))}
      </div>
    </div>
  );
}